import { RECIPES } from "../content/recipes";
import { isRecipeOwned } from "./crafting";
import { dailyOpsCompleted, dailyOpsShortLabel } from "./daily-ops";
import { extractHeatBonusPct } from "./extract-heat";
import type { BankedProfile } from "./persistence";
import type { RunSummary } from "./scoring";
import { SORTIE_CONTRACTS } from "./sortie-contracts";
import {
  CALIBRATION_AXIS_LABEL,
  WEAPON_CALIBRATION_AXES,
  calibrationOffer,
} from "./weapon-calibration";
import { WEAPONS } from "../content/weapons";
export type RunHookKind = "rank" | "unlock" | "ops" | "heat" | "pb";

export type RunHook = {
  kind: RunHookKind;
  text: string;
  urgency: number;
};

export type NextUnlock = {
  id: string;
  name: string;
  cost: number;
  remaining: number;
  rankRequired: number;
  rankLocked: boolean;
  armed?: boolean;
  action?: "craft" | "arm" | "tune";
};

function nextCalibrationUnlock(profile: BankedProfile): NextUnlock | null {
  let best: NextUnlock | null = null;
  for (const weaponId of profile.selectedWeapons) {
    const weapon = WEAPONS[weaponId];
    if (!weapon) continue;
    for (const axis of WEAPON_CALIBRATION_AXES) {
      const offer = calibrationOffer(profile, weaponId, axis);
      if (offer.maxed) continue;
      const candidate: NextUnlock = {
        id: `calibration_${weaponId}_${axis}`,
        name: `${weapon.name} ${CALIBRATION_AXIS_LABEL[axis]} Lv ${offer.nextLevel}`,
        cost: offer.cost,
        remaining: Math.max(0, offer.cost - profile.salvage),
        rankRequired: offer.requiredRank,
        rankLocked: offer.requiredRank > profile.pilotMeta.rank,
        action: "tune",
      };
      if (!best) {
        best = candidate;
        continue;
      }
      if (candidate.rankLocked !== best.rankLocked) {
        if (!candidate.rankLocked) best = candidate;
        continue;
      }
      if (candidate.rankRequired !== best.rankRequired) {
        if (candidate.rankRequired < best.rankRequired) best = candidate;
        continue;
      }
      if (candidate.remaining < best.remaining) best = candidate;
    }
  }
  return best;
}

export function nextCraftUnlock(profile: BankedProfile): NextUnlock | null {
  let best: NextUnlock | null = null;
  for (const recipe of RECIPES) {
    if (isRecipeOwned(profile, recipe)) continue;
    if (recipe.requiresBlueprint && !profile.blueprints.includes(recipe.requiresBlueprint)) {
      continue;
    }
    const remaining = Math.max(0, recipe.salvageCost - profile.salvage);
    const rankRequired = recipe.requiresRank ?? 1;
    const candidate: NextUnlock = {
      id: recipe.id,
      name: recipe.name,
      cost: recipe.salvageCost,
      remaining,
      rankRequired,
      rankLocked: rankRequired > profile.pilotMeta.rank,
    };
    if (!best) {
      best = candidate;
      continue;
    }
    const candidateRankLocked = rankRequired > profile.pilotMeta.rank;
    const bestRankLocked = best.rankRequired > profile.pilotMeta.rank;
    if (candidateRankLocked !== bestRankLocked) {
      if (!candidateRankLocked) best = candidate;
      continue;
    }
    if (candidateRankLocked && rankRequired !== best.rankRequired) {
      if (rankRequired < best.rankRequired) best = candidate;
      continue;
    }
    if (remaining !== best.remaining) {
      if (remaining < best.remaining) best = candidate;
      continue;
    }
    if (recipe.salvageCost < best.cost) best = candidate;
  }
  const calibration = nextCalibrationUnlock(profile);
  if (best && (!best.rankLocked || !calibration || calibration.rankLocked)) return best;
  if (calibration) return calibration;

  const contracts = Object.values(SORTIE_CONTRACTS);
  const contract =
    contracts
      .filter((entry) => entry.minRank > profile.pilotMeta.rank)
      .sort((a, b) => a.minRank - b.minRank)[0] ??
    contracts
      .filter((entry) => entry.minRank <= profile.pilotMeta.rank)
      .sort((a, b) => b.minRank - a.minRank)[0] ??
    SORTIE_CONTRACTS.redline;
  return {
    id: `contract_${contract.id}`,
    name: contract.name,
    cost: contract.salvageCost,
    remaining: Math.max(0, contract.salvageCost - profile.salvage),
    rankRequired: contract.minRank,
    rankLocked: contract.minRank > profile.pilotMeta.rank,
    armed: profile.queuedSortieContract === contract.id,
    action: "arm",
  };
}

export function runHookLines(profile: BankedProfile, summary: RunSummary | null): RunHook[] {
  const hooks: RunHook[] = [];
  const meta = profile.pilotMeta;
  if (meta.rankPointsToNext > 0) {
    const remain = Math.max(0, meta.rankPointsToNext - meta.rankPoints);
    const pct = meta.rankPoints / meta.rankPointsToNext;
    if (remain > 0 && pct >= 0.55) {
      hooks.push({
        kind: "rank",
        text: `${remain} RP to Rank ${meta.rank + 1}`,
        urgency: pct >= 0.85 ? 3 : 2,
      });
    }
  }

  const unlock = nextCraftUnlock(profile);
  if (unlock) {
    if (unlock.armed) {
      hooks.push({
        kind: "unlock",
        text: `${unlock.name} armed — launch when ready`,
        urgency: 2,
      });
    } else if (unlock.rankRequired > profile.pilotMeta.rank) {
      hooks.push({
        kind: "unlock",
        text: `${unlock.name} unlocks at Rank ${unlock.rankRequired}`,
        urgency: 1,
      });
    } else if (unlock.remaining === 0) {
      hooks.push({
        kind: "unlock",
        text: `${unlock.name} is ready to ${unlock.action ?? "craft"}`,
        urgency: 3,
      });
    } else if (unlock.remaining <= 40) {
      hooks.push({
        kind: "unlock",
        text: `${unlock.remaining} salvage to ${unlock.name}`,
        urgency: unlock.remaining <= 16 ? 3 : 2,
      });
    }
  }

  const ops = profile.dailyOps;
  if (ops) {
    const open = ops.ops.find((op) => !op.claimed && op.progress < op.target);
    if (open && open.progress / open.target >= 0.5) {
      hooks.push({
        kind: "ops",
        text: `${dailyOpsShortLabel(open)} — finish today's ops`,
        urgency: open.progress / open.target >= 0.75 ? 3 : 2,
      });
    } else if (dailyOpsCompleted(ops) === 0) {
      hooks.push({
        kind: "ops",
        text: "Daily ops waiting — launch again",
        urgency: 1,
      });
    }
  }

  if (summary?.outcome === "extracted" && profile.extractStreak >= 2) {
    hooks.push({
      kind: "heat",
      text: `Heat ${profile.extractStreak} · +${extractHeatBonusPct(profile.extractStreak)}% salvage`,
      urgency: 2,
    });
  } else if (summary?.outcome === "dead" && profile.bestExtractStreak >= 2) {
    hooks.push({
      kind: "heat",
      text: "Heat reset — extract to rebuild the streak",
      urgency: 2,
    });
  }

  if (summary && summary.score > 0 && summary.score < meta.personalBestScore) {
    const delta = meta.personalBestScore - summary.score;
    if (delta <= meta.personalBestScore * 0.2) {
      hooks.push({
        kind: "pb",
        text: `${delta.toLocaleString()} short of your PB`,
        urgency: 3,
      });
    }
  }

  return hooks.sort((a, b) => b.urgency - a.urgency).slice(0, 4);
}

export function formatNextUnlock(unlock: NextUnlock | null): string {
  if (!unlock) return "All recipes owned";
  if (unlock.armed) return `${unlock.name} — armed`;
  if (unlock.rankLocked) return `Rank ${unlock.rankRequired} · ${unlock.name}`;
  if (unlock.remaining === 0) return `${unlock.name} — ${unlock.action ?? "craft"} now`;
  return `${unlock.remaining} salvage to ${unlock.name}`;
}
